import { motion } from "framer-motion";
import { ArrowUpRight, Send } from "lucide-react";
import { useI18n, pick } from "@/lib/i18n";
import { useContent } from "@/lib/content";

export default function Vacancies() {
  const { lang } = useI18n();
  const { content } = useContent();
  const v = content?.vacancies;
  if (!v) return null;
  const header = pick(v, lang, v.ru);
  const items = v.items || [];
  return (
    <section id="vacancies" className="py-16 sm:py-20 lg:py-32" data-testid="vacancies-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-4">
            {lang === "ru" ? "Вакансии" : "Vacancies"}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-semibold tracking-tight">{header.title}</h2>
          <p className="mt-4 text-base text-muted-foreground">{header.subtitle}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6 mb-12">
          {items.map((it, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group p-5 sm:p-6 lg:p-8 rounded-2xl border border-border bg-card hover:border-foreground/30 transition-colors"
              data-testid={`vacancy-${i}`}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-heading text-xl font-semibold">
                  {lang === "ru" ? it.ru_title : it.en_title}
                </h3>
                <ArrowUpRight className="size-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {lang === "ru" ? it.ru_desc : it.en_desc}
              </p>
              <ul className="space-y-2">
                {(lang === "ru" ? it.ru_bullets : it.en_bullets).map((b, j) => (
                  <li key={j} className="text-sm flex items-center gap-2">
                    <span className="size-1 rounded-full bg-accent" />
                    {b}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden p-6 sm:p-8 lg:p-12 rounded-3xl border border-border bg-foreground text-background"
        >
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-accent/30 blur-3xl" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h3 className="font-heading text-2xl lg:text-3xl font-bold mb-2">{header.cta_title}</h3>
              <p className="text-sm opacity-80">{header.cta_text}</p>
            </div>
            <a
              href={v.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-accent text-accent-foreground font-semibold text-sm hover:translate-y-[-2px] transition-transform"
              data-testid="vacancies-telegram-btn"
            >
              <Send className="size-4" />
              {header.cta_button}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
